import {
  YAK_TRAILS_PROTOCOL_VERSION,
  type GameSnapshot,
  type RoomSpec,
  type RunResult,
  type SimulationEvent,
  type SteeringInput,
  type TerritoryPatch,
} from "./domain";

export const YAK_TRAILS_SOCKET_CLOSE_CODES = {
  connectionReplaced: 4_000,
  arenaClosed: 4_001,
  clientHealthRestart: 4_100,
} as const;

export interface TerminalRoomSocketClose {
  code: "session_replaced" | "arena_closed" | "protocol_error";
  message: string;
}

/** Shared server/client close semantics prevent terminal rooms from reconnecting forever. */
export function terminalRoomSocketClose(code: number, reason: string): TerminalRoomSocketClose | undefined {
  if (code === YAK_TRAILS_SOCKET_CLOSE_CODES.connectionReplaced && reason === "connection_replaced") {
    return {
      code: "session_replaced",
      message: "Another tab opened this Yak Trails room. Continue there or leave this arena.",
    };
  }
  if (code === YAK_TRAILS_SOCKET_CLOSE_CODES.arenaClosed && reason === "arena_closed") {
    return {
      code: "arena_closed",
      message: "This arena has closed. Leave or rejoin a fresh round.",
    };
  }
  if (
    (code === 1_002 && reason === "protocol_error")
    || (code === 1_003 && reason === "binary_required")
    || (code === 1_009 && reason === "frame_too_large")
  ) {
    return {
      code: "protocol_error",
      message: "This arena uses an incompatible real-time protocol. Refresh Yak Trails and try again.",
    };
  }
  return undefined;
}

export type ClientMessage =
  | { v: 1; type: "input"; input: SteeringInput }
  | { v: 1; type: "respawn"; seq: number }
  | { v: 1; type: "resync"; seq: number }
  | { v: 1; type: "ping"; nonce: number; clientTimeMs: number };

export type ServerMessage =
  | { v: 1; type: "welcome"; playerId: string; room: RoomSpec; reconnectToken?: string }
  | { v: 1; type: "snapshot"; snapshot: GameSnapshot; territory?: TerritoryPatch }
  | { v: 1; type: "territory"; patch: TerritoryPatch }
  | { v: 1; type: "events"; events: SimulationEvent[] }
  | { v: 1; type: "result"; result: RunResult }
  | { v: 1; type: "pong"; nonce: number; clientTimeMs: number; serverTimeMs: number }
  | { v: 1; type: "error"; code: string; message: string; retryable: boolean };

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const clientTags: Record<ClientMessage["type"], number> = {
  input: 1,
  respawn: 2,
  resync: 3,
  ping: 4,
};

const serverTags: Record<ServerMessage["type"], number> = {
  welcome: 21,
  snapshot: 22,
  territory: 23,
  events: 24,
  result: 25,
  pong: 26,
  error: 27,
};

const clientTypes = new Map(Object.entries(clientTags).map(([type, tag]) => [tag, type as ClientMessage["type"]]));
const serverTypes = new Map(Object.entries(serverTags).map(([type, tag]) => [tag, type as ServerMessage["type"]]));

export class YakTrailsProtocolError extends Error {}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isSequence(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function validateClientMessage(value: unknown): asserts value is ClientMessage {
  if (!isRecord(value) || typeof value.type !== "string") throw new YakTrailsProtocolError("Client frame body has the wrong shape");
  if (value.type === "input") {
    if (!isRecord(value.input) || !isSequence(value.input.seq) || !isSequence(value.input.clientTick)
      || !isFiniteNumber(value.input.targetX) || !isFiniteNumber(value.input.targetY)) {
      throw new YakTrailsProtocolError("Steering input has the wrong shape");
    }
    return;
  }
  if (value.type === "respawn" || value.type === "resync") {
    if (!isSequence(value.seq)) throw new YakTrailsProtocolError("Command sequence has the wrong shape");
    return;
  }
  if (value.type === "ping") {
    if (!isSequence(value.nonce) || !isFiniteNumber(value.clientTimeMs)) throw new YakTrailsProtocolError("Ping has the wrong shape");
    return;
  }
  throw new YakTrailsProtocolError("Unknown client frame body");
}

function validateServerMessage(value: unknown): asserts value is ServerMessage {
  if (!isRecord(value) || typeof value.type !== "string") throw new YakTrailsProtocolError("Server frame body has the wrong shape");
  if (value.type === "welcome") {
    if (typeof value.playerId !== "string" || !isRecord(value.room)
      || (value.reconnectToken !== undefined && typeof value.reconnectToken !== "string")) {
      throw new YakTrailsProtocolError("Welcome frame has the wrong shape");
    }
    return;
  }
  if (value.type === "snapshot") {
    if (!isRecord(value.snapshot) || !Array.isArray(value.snapshot.players) || !Array.isArray(value.snapshot.trails)
      || !Array.isArray(value.snapshot.leaderboard) || (value.territory !== undefined && !isRecord(value.territory))) {
      throw new YakTrailsProtocolError("Snapshot frame has the wrong shape");
    }
    return;
  }
  if (value.type === "territory") {
    if (!isRecord(value.patch) || !Array.isArray(value.patch.changedRuns)) throw new YakTrailsProtocolError("Territory frame has the wrong shape");
    return;
  }
  if (value.type === "events") {
    if (!Array.isArray(value.events)) throw new YakTrailsProtocolError("Events frame has the wrong shape");
    return;
  }
  if (value.type === "result") {
    if (!isRecord(value.result)) throw new YakTrailsProtocolError("Result frame has the wrong shape");
    return;
  }
  if (value.type === "pong") {
    if (!isSequence(value.nonce) || !isFiniteNumber(value.clientTimeMs) || !isFiniteNumber(value.serverTimeMs)) {
      throw new YakTrailsProtocolError("Pong frame has the wrong shape");
    }
    return;
  }
  if (value.type === "error") {
    if (typeof value.code !== "string" || typeof value.message !== "string" || typeof value.retryable !== "boolean") {
      throw new YakTrailsProtocolError("Error frame has the wrong shape");
    }
    return;
  }
  throw new YakTrailsProtocolError("Unknown server frame body");
}

function encodeMessage(message: ClientMessage | ServerMessage, tag: number): Uint8Array {
  const body = encoder.encode(JSON.stringify(message));
  const frame = new Uint8Array(body.length + 2);
  frame[0] = YAK_TRAILS_PROTOCOL_VERSION;
  frame[1] = tag;
  frame.set(body, 2);
  return frame;
}

function normalizeFrame(frame: ArrayBuffer | Uint8Array): Uint8Array {
  return frame instanceof Uint8Array ? frame : new Uint8Array(frame);
}

function decodeMessage<T extends ClientMessage | ServerMessage>(
  frame: ArrayBuffer | Uint8Array,
  types: Map<number, T["type"]>,
  maximumFrameBytes: number,
): T {
  const bytes = normalizeFrame(frame);
  if (bytes.byteLength < 3) throw new YakTrailsProtocolError("Protocol frame is truncated");
  if (bytes.byteLength > maximumFrameBytes) throw new YakTrailsProtocolError("Protocol frame is too large");
  if (bytes[0] !== YAK_TRAILS_PROTOCOL_VERSION) throw new YakTrailsProtocolError("Unsupported protocol version");
  const expectedType = types.get(bytes[1] ?? -1);
  if (!expectedType) throw new YakTrailsProtocolError("Unknown protocol message tag");
  let parsed: unknown;
  try {
    parsed = JSON.parse(decoder.decode(bytes.subarray(2)));
  } catch {
    throw new YakTrailsProtocolError("Protocol frame body is not valid JSON");
  }
  if (!parsed || typeof parsed !== "object") throw new YakTrailsProtocolError("Protocol frame body must be an object");
  const candidate = parsed as { v?: unknown; type?: unknown };
  if (candidate.v !== YAK_TRAILS_PROTOCOL_VERSION || candidate.type !== expectedType) {
    throw new YakTrailsProtocolError("Protocol tag and body do not match");
  }
  return parsed as T;
}

export function encodeClientMessage(message: ClientMessage): Uint8Array {
  return encodeMessage(message, clientTags[message.type]);
}

export function decodeClientMessage(frame: ArrayBuffer | Uint8Array, maximumFrameBytes = 256 * 1024): ClientMessage {
  const message = decodeMessage<ClientMessage>(frame, clientTypes, maximumFrameBytes);
  validateClientMessage(message);
  return message;
}

export function encodeServerMessage(message: ServerMessage): Uint8Array {
  return encodeMessage(message, serverTags[message.type]);
}

export function decodeServerMessage(frame: ArrayBuffer | Uint8Array, maximumFrameBytes = 256 * 1024): ServerMessage {
  const message = decodeMessage<ServerMessage>(frame, serverTypes, maximumFrameBytes);
  validateServerMessage(message);
  return message;
}
